import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import {
  AdjustmentSettingsIcon,
  ChevronArrowRightIcon,
  CommissionRulesTabIcon,
  EstimatesTabIcon,
  ExternalCompanyIcon,
  InvoicesTabIcon,
  ManageTagsIcon,
  PayScheduleTabIcon,
  PaymentsTabIcon,
  PayoutsTabIcon,
  SavedViewsTabIcon,
  ServiceAreaIcon,
  TeamManagementTabIcon,
  TeamProfileTabIcon
} from 'assets/icons';
import classNames from 'classnames';
import { useAppSelector } from 'hooks/typedHooks';
import {
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamJobPermissions,
  selectShowEstimatesSideNav
} from 'containers/app/appSlice';
import { logAnalyticEvent } from 'utils/analytics';
import { SettingsCleverTapEvents } from './events';

interface SettingItem {
  id: string;
  title: string;
  path: string;
  section: string;
  icon: React.FC<{ className?: string }>;
}

const SETTINGS_ITEMS: SettingItem[] = [
  // General section
  {
    id: 'team_profile',
    title: 'Team profile',
    path: routesPath.TEAM_PROFILE,
    section: 'General',
    icon: TeamProfileTabIcon
  },
  {
    id: 'team_management',
    title: 'Team management',
    path: routesPath.MANAGE_TEAM,
    section: 'General',
    icon: TeamManagementTabIcon
  },
  // Job settings section
  {
    id: 'tags',
    title: 'Tags',
    path: routesPath.TAGS,
    section: 'Job settings',
    icon: ManageTagsIcon
  },
  {
    id: 'saved_views',
    title: 'Saved views',
    path: routesPath.MAP_VIEWS_SETTINGS,
    section: 'Job settings',
    icon: SavedViewsTabIcon
  },
  {
    id: 'external_companies',
    title: 'External companies',
    path: routesPath.EXTERNAL_COMPANY_SETTINGS,
    section: 'Job settings',
    icon: ExternalCompanyIcon
  },
  {
    id: 'service_areas',
    title: 'Service areas',
    path: routesPath.SERVICE_AREAS_SETTINGS,
    section: 'Job settings',
    icon: ServiceAreaIcon
  },
  {
    id: 'estimate_settings',
    title: 'Estimates',
    path: routesPath.ESTIMATE_SETTINGS,
    section: 'Billing and payments',
    icon: EstimatesTabIcon
  },
  {
    id: 'invoice_settings',
    title: 'Invoices',
    path: routesPath.INVOICE_SETTINGS,
    section: 'Billing and payments',
    icon: InvoicesTabIcon
  },
  {
    id: 'payment_settings',
    title: 'Payments',
    path: routesPath.PAYMENT_SETTINGS,
    section: 'Billing and payments',
    icon: PaymentsTabIcon
  },
  {
    id: 'payouts',
    title: 'Payouts',
    path: routesPath.PAYOUTS_SETTINGS,
    section: 'Billing and payments',
    icon: PayoutsTabIcon
  },
  // Accounting and Pay section
  {
    id: 'commission_rules',
    title: 'Commission rules',
    path: routesPath.COMMISSION_SETTINGS,
    section: 'Accounting and Pay',
    icon: CommissionRulesTabIcon
  },
  {
    id: 'pay_schedule',
    title: 'Pay schedule',
    path: routesPath.PAY_SCHEDULE_SETTINGS,
    section: 'Accounting and Pay',
    icon: PayScheduleTabIcon
  },
  {
    id: 'adjustment_settings',
    title: 'Adjustments',
    path: routesPath.ADJUSTMENT_SETTINGS,
    section: 'Accounting and Pay',
    icon: AdjustmentSettingsIcon
  }
];

const Settings: FC = () => {
  const navigate = useNavigate();
  const showEstimateSettings = useAppSelector(selectShowEstimatesSideNav);
  const { viewAccountingSettings } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { viewJobSource, editJobSource } = useAppSelector(selectCurrentTeamJobPermissions);

  const groupedSettings: Record<string, SettingItem[]> = useMemo(() => {
    const filtered = SETTINGS_ITEMS.filter(item => {
      if (item.id === 'estimate_settings') {
        return showEstimateSettings;
      }
      if (item.section === 'Accounting and Pay') {
        return viewAccountingSettings;
      }
      if (item.id === 'external_companies') {
        return viewJobSource || editJobSource;
      }
      return true;
    });

    return filtered.reduce((acc, item) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section].push(item);
      return acc;
    }, {});
  }, [showEstimateSettings, viewAccountingSettings, viewJobSource, editJobSource]);

  return (
    <div className="h-full w-full">
      <div className="px-[30px] pb-16 pt-6">
        <div className="mb-6 text-2xl font-bold text-primaryText">Settings</div>
        <div className="flex flex-col gap-12">
          {Object.entries(groupedSettings).map(([section, items]) => (
            <div key={section} className="flex flex-col gap-3">
              <div className="text-sbase font-semibold text-accentText">{section}</div>
              <div className="grid grid-cols-2 gap-5">
                {items.map((item: SettingItem) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      navigate(item.path);
                      logAnalyticEvent(SettingsCleverTapEvents.webSettingsClick, {
                        section: section,
                        title: item.title
                      });
                    }}
                    className={classNames(
                      'flex cursor-pointer items-center justify-between border border-borderGray rounded p-3 w-full'
                    )}>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <item.icon className="h-5 w-5 text-primaryText" />
                        <div className="text-17px font-bold text-primaryText">{item.title}</div>
                      </div>
                    </div>
                    <ChevronArrowRightIcon className="h-6 w-6 text-accentText" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
